import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import DashboardGrid from "./Dashboard/DashboardGrid";
import AddWidgetDrawer from "./Dashboard/AddWidgetDrawer";
import { defaultDashboard } from "../config/defaultDashboard";
import { getEdges } from "../api/edgeApi";
import { getLatestMetrics, getEdgeAlarms, getEdgeAnomalies } from "../api/metricsApi";
import { eventRangeChoice, loadEventRanges, resolveEventRange, saveEventRange } from "../utils/eventRange";
import { formatViewerDateTime, getViewerTimeZone } from "../utils/viewerTimeZone";
import {
  saveDashboardLayout,
  getDashboardLayout,
  getDashboardEdges,
  getDashboardEdgeDevices,
  getDashboardEdgeDeviceTags,
  getDashboardDataLatest,
  getDashboardDataSeries,
  getDashboardDataStats,
  getDashboardEdgeSystem,
} from "../api/dashboardApi";
import { getTenants } from "../api/tenantApi";
import { getVpnUsers } from "../api/vpnApi";
import { apiClient } from "../api/apiClient";
import { toast } from "react-toastify";

export default function Home() {
  const { t } = useTranslation();
  const accessToken = useSelector((s) => s.session?.accessToken);
  const me = useSelector((s) => s.session?.me);
  const isSuperAdmin = me?.global_role === "superadmin";
  const canEditDashboard = me?.tenant_role === "admin";
  const isOperator = me?.tenant_role === "operatör";
  const [searchParams] = useSearchParams();

  // Superadmin istatistikleri
  const [adminStats, setAdminStats] = useState({
    edgeCount: null,
    tenantCount: null,
    userCount: null,
    vpnUserCount: null,
  });

  useEffect(() => {
    if (!isSuperAdmin || !accessToken) return;

    async function fetchAdminStats() {
      const [edgeRes, tenantRes, vpnRes, userRes] = await Promise.allSettled([
        getEdges(accessToken),
        getTenants(accessToken, { limit: 1 }),
        getVpnUsers(accessToken, { limit: 1 }),
        apiClient.get("/auth/users/list?limit=1", { headers: { Authorization: `Bearer ${accessToken}` } }),
      ]);

      setAdminStats({
        edgeCount:    edgeRes.status   === "fulfilled" ? (edgeRes.value?.total   ?? edgeRes.value?.length   ?? 0) : 0,
        tenantCount:  tenantRes.status === "fulfilled" ? (tenantRes.value?.total ?? tenantRes.value?.length ?? 0) : 0,
        vpnUserCount: vpnRes.status    === "fulfilled" ? (vpnRes.value?.total    ?? vpnRes.value?.length    ?? 0) : 0,
        userCount:    userRes.status   === "fulfilled" ? (userRes.value?.data?.total ?? 0) : 0,
      });
    }

    fetchAdminStats();
  }, [isSuperAdmin, accessToken]);

  const [timeRange, setTimeRange] = useState("15m");
  // each alarm / anomaly table has its own range (utils/eventRange.js); a per-viewer choice kept in localStorage
  const [eventRanges, setEventRanges] = useState(() => loadEventRanges(window.localStorage));
  const changeEventRange = (widgetId, choice) => {
    saveEventRange(window.localStorage, widgetId, choice);
    setEventRanges((prev) => ({ ...prev, [widgetId]: choice }));
  };
  const [refreshInterval, setRefreshInterval] = useState("5s");
  const [edges, setEdges] = useState([]);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [dashboard, setDashboard] = useState(defaultDashboard);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1200);

  // Edge'den cihaz/tag seçimi (widget eklerken)
  const [dashboardDevices, setDashboardDevices] = useState([]);
  const [deviceTags, setDeviceTags] = useState([]);
  const [widgetData, setWidgetData] = useState({});
  const [addWidgetDeviceIds, setAddWidgetDeviceIds] = useState([]);
  const [addWidgetTagNames, setAddWidgetTagNames] = useState([]);
  const [addDrawerOpen, setAddDrawerOpen] = useState(false);
  const [devicesError, setDevicesError] = useState(null);

  // Mock sensor data - InfluxDB'den gelecek
  const [sensorData, setSensorData] = useState({
    edgeId: "edge-001",
    temperature: 54.8,
    vibration: 25.5,
    pressure: 104,
    voltage: 223,
    current: 11.8,
    cpu: 0.877,
    ram: 15.2,
    disk: 0.608,
  });

  // Container width hesapla
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  // Dashboard için erişilebilir edge'leri yükle (tenant admin kendi edge'lerini görür)
  useEffect(() => {
    if (!accessToken) return;

    async function fetchEdges() {
      try {
        const items = await getDashboardEdges(accessToken);
        const list = Array.isArray(items) ? items : [];
        const normalized = list.map((e) => ({
          id: e.id,
          edgeId: e.edge_id ?? e.edgeId,
          name: e.name ?? e.edge_id ?? e.edgeId ?? e.id,
        }));
        setEdges(normalized);
        if (normalized.length > 0) {
          const targetId = searchParams.get("edgeId");
          const target = targetId ? normalized.find((e) => e.id === targetId || e.edgeId === targetId) : null;
          const selected = target || normalized[0];
          setSelectedEdge(selected);
          setSensorData((prev) => ({ ...prev, edgeId: selected.edgeId }));
        }
      } catch (err) {
        console.error("Could not load the dashboard edge list:", err);
        setEdges([]);
      }
    }

    fetchEdges();
  }, [accessToken, searchParams]);

  // InfluxDB'den sensör verilerini çek
  const fetchSensorData = useCallback(async () => {
    if (!accessToken || !selectedEdge) return;

    try {
      const data = await getLatestMetrics(accessToken, selectedEdge.edgeId);
      // Do NOT fall back to the previous edge's values here — an empty/no-data
      // response for the newly selected edge must clear the screen, not keep
      // showing whatever the last edge had (was leaking stale numbers across
      // edge switches, reported live 2026-09-28).
      setSensorData((prev) => ({
        ...prev,
        edgeId: selectedEdge.edgeId,
        temperature: data?.temperature ?? null,
        vibration: data?.vibration ?? null,
        pressure: data?.pressure ?? null,
        voltage: data?.voltage ?? null,
        current: data?.current ?? null,
      }));
    } catch (err) {
      console.error("Could not load sensor data:", err);
    }
  }, [accessToken, selectedEdge]);

  // Seçilen edge'in cihaz listesi (dashboard widget eklerken)
  useEffect(() => {
    if (!accessToken || !selectedEdge?.edgeId) {
      setDashboardDevices([]);
      setDevicesError(null);
      return;
    }
    setDevicesError(null);
    getDashboardEdgeDevices(accessToken, selectedEdge.edgeId)
      .then((list) => {
        setDashboardDevices(Array.isArray(list) ? list : []);
        setDevicesError(null);
      })
      .catch((err) => {
        setDashboardDevices([]);
        const msg = err.response?.data?.detail || err.message || t("home.toast.devicesFailed");
        setDevicesError(typeof msg === "string" ? msg : JSON.stringify(msg));
        toast.error(msg);
      });
  }, [accessToken, selectedEdge?.edgeId, t]);

  // Seçilen cihaz(lar) için tag listesi
  useEffect(() => {
    if (!accessToken || !selectedEdge?.edgeId || addWidgetDeviceIds.length === 0) {
      setDeviceTags([]);
      return;
    }
    const load = async () => {
      const all = [];
      for (const deviceId of addWidgetDeviceIds) {
        try {
          const tags = await getDashboardEdgeDeviceTags(accessToken, selectedEdge.edgeId, deviceId);
          all.push(...(tags || []).map((tg) => ({ ...tg, deviceId })));
        } catch (_) {}
      }
      const seen = new Set();
      setDeviceTags(all.filter((tg) => {
        const k = tg.name;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      }));
    };
    load();
  }, [accessToken, selectedEdge?.edgeId, addWidgetDeviceIds]);

  // Cihaz/tag seçimli + edge_system widget'lar için veri çek
  const fetchWidgetData = useCallback(async () => {
    const widgets = dashboard?.widgets ?? [];
    const withEdge = widgets.filter(
      (w) => w?.config?.edgeId && w?.config?.deviceIds?.length && w?.config?.tagNames?.length
    );
    const systemWidgets = widgets.filter((w) => w.type === "edge_system" && w?.config?.edgeId);
    const logsWidgets = widgets.filter((w) => w.type === "edge_logs" && w?.config?.edgeId);
    const alarmWidgets = widgets.filter((w) => w.type === "alarm_table" && w?.config?.edgeId);
    const anomalyWidgets = widgets.filter((w) => w.type === "anomaly_table" && w?.config?.edgeId);
    const feedWidgets = widgets.filter((w) => w.type === "event_feed" && w?.config?.edgeId);
    const next = {};
    if (!withEdge.length && !systemWidgets.length && !logsWidgets.length && !alarmWidgets.length && !anomalyWidgets.length && !feedWidgets.length) return;
    for (const w of withEdge) {
      try {
        const needsSeries = w.type === "linechart" || w.type === "table" || w.type === "kpi";
        // gauge without explicit min/max: dial range comes from the last 24h (resolveGaugeRange)
        const needsStats = w.type === "gauge" && (w.config.min == null || w.config.max == null);
        const [latest, seriesRes, statsRes] = await Promise.all([
          getDashboardDataLatest(accessToken, w.config.edgeId, w.config.deviceIds, w.config.tagNames),
          needsSeries
            ? getDashboardDataSeries(
                accessToken,
                w.config.edgeId,
                w.config.deviceIds,
                w.config.tagNames,
                timeRange
              )
            : Promise.resolve(null),
          needsStats
            ? getDashboardDataStats(accessToken, w.config.edgeId, w.config.deviceIds, w.config.tagNames, "24h").catch(() => null)
            : Promise.resolve(null),
        ]);
        next[w.id] = {
          latest: Array.isArray(latest) ? latest : [],
          series: seriesRes?.data || [],
          stats: statsRes?.data || [],
        };
      } catch (_) {
        next[w.id] = { latest: [], series: [], stats: [] };
      }
    }
    for (const w of systemWidgets) {
      try {
        const data = await getDashboardEdgeSystem(accessToken, w.config.edgeId);
        next[w.id] = { system: data };
      } catch (_) {
        next[w.id] = { system: null };
      }
    }
    for (const w of logsWidgets) {
      try {
        const data = await getDashboardEdgeSystem(accessToken, w.config.edgeId);
        if (data) {
          const ts = data.ts ?? data.timestamp;
          setWidgetData((prev) => {
            const existing = prev[w.id]?.logs ?? [];
            const alreadyExists = ts && existing.some((e) => (e.ts ?? e.timestamp) === ts);
            if (alreadyExists) return prev;
            const newEntry = { ...data, fetchedAt: new Date().toISOString() };
            return { ...prev, [w.id]: { logs: [...existing, newEntry].slice(-500) } };
          });
        }
      } catch (_) {}
    }
    // Alarm / anomaly tables: each one asks for ITS range (per edge and range one request). A table that comes back empty also looks for
    // the newest event of that edge in the last 30 days, so it can say "Son anomali: ..." and offer the wider range.
    const mapAlarm = (a) => ({
      time: formatViewerDateTime(a.time),
      field: a.field ?? a.metric ?? "-",
      severity: a.severity ?? "-",
      value: a.value ?? "-",
      threshold: a.threshold ?? "-",
      message: a.message ?? "",
    });
    const mapAnomaly = (a) => ({
      time: formatViewerDateTime(a.time),
      field: a.field ?? "-",
      level: a.level ?? "-",
      value: a.value ?? "-",
      score: a.score ?? "-",
    });
    const fillEventTables = async (tables, fetcher, map, key) => {
      const cache = new Map();
      const load = (edgeId, range, limit) => {
        const id = `${edgeId}:${range}:${limit}`;
        if (!cache.has(id)) cache.set(id, fetcher(accessToken, edgeId, range, limit).then((r) => (Array.isArray(r) ? r : [])).catch(() => []));
        return cache.get(id);
      };
      for (const w of tables) {
        const choice = eventRangeChoice(w.id, eventRanges, w.config);
        const range = resolveEventRange(choice, timeRange);
        const raw = await load(w.config.edgeId, range, 50);
        const lastAt = raw.length ? null : ((await load(w.config.edgeId, "30d", 1))[0]?.time || null);
        next[w.id] = { ...(next[w.id] || {}), [key]: raw.map(map), choice, range, lastAt };
      }
    };
    if (alarmWidgets.length) await fillEventTables(alarmWidgets, getEdgeAlarms, mapAlarm, "alarms");
    if (anomalyWidgets.length) await fillEventTables(anomalyWidgets, getEdgeAnomalies, mapAnomaly, "anomalies");
    // "Olay akışı": alarms + anomalies of one edge merged newest-first, same per-widget range as the tables
    for (const w of feedWidgets) {
      const choice = eventRangeChoice(w.id, eventRanges, w.config);
      const range = resolveEventRange(choice, timeRange);
      const get = (fetcher, limit) =>
        fetcher(accessToken, w.config.edgeId, range, limit).then((r) => (Array.isArray(r) ? r : [])).catch(() => []);
      const [al, an] = await Promise.all([get(getEdgeAlarms, 50), get(getEdgeAnomalies, 50)]);
      const events = [
        ...al.map((a) => ({
          kind: "alarm", rawTime: a.time, time: formatViewerDateTime(a.time),
          field: a.field ?? a.metric ?? "-", value: a.value ?? "-", detail: a.message ?? a.severity ?? "",
        })),
        ...an.map((a) => ({
          kind: "anomali", rawTime: a.time, time: formatViewerDateTime(a.time),
          field: a.field ?? "-", value: a.value ?? "-", detail: a.level ? t("home.feed.score", { level: a.level, score: a.score ?? "-" }) : "",
        })),
      ].sort((x, y) => (x.rawTime < y.rawTime ? 1 : -1));
      next[w.id] = { events, choice, range, lastAt: null };
    }
    setWidgetData((prev) => ({ ...prev, ...next }));
  }, [accessToken, dashboard?.widgets, timeRange, eventRanges, t]);

  useEffect(() => {
    fetchWidgetData();
  }, [fetchWidgetData]);

  // İlk yükleme ve edge değiştiğinde veri çek
  useEffect(() => {
    fetchSensorData();
  }, [fetchSensorData]);

  // Periyodik veri güncelleme
  useEffect(() => {
    if (refreshInterval === "off") return;

    const intervalMs = {
      "5s": 5000,
      "10s": 10000,
      "30s": 30000,
      "1m": 60000,
    }[refreshInterval] || 5000;

    const interval = setInterval(() => {
      fetchSensorData();
      fetchWidgetData();
    }, intervalMs);

    return () => clearInterval(interval);
  }, [refreshInterval, fetchSensorData, fetchWidgetData]);

  // Layout değişikliğini handle et
  const handleLayoutChange = (newLayout) => {
    const updatedWidgets = dashboard.widgets.map((widget) => {
      const layoutItem = newLayout.find((l) => l.id === widget.id);
      if (layoutItem) {
        return { ...widget, position: layoutItem.position };
      }
      return widget;
    });

    setDashboard((prev) => ({ ...prev, widgets: updatedWidgets }));
  };

  // Dashboard'u backend'e kaydet
  const saveDashboard = async () => {
    try {
      await saveDashboardLayout(accessToken, JSON.stringify(dashboard));
      setEditMode(false);
      toast.success(t("home.toast.saved"));
    } catch (err) {
      console.error("Could not save the dashboard:", err);
      toast.error(t("home.toast.saveFailed"));
    }
  };

  // Dashboard'u backend'den yükle
  useEffect(() => {
    if (!accessToken) return;

    async function fetchLayout() {
      try {
        const data = await getDashboardLayout(accessToken);
        if (data && data.layout_json) {
          setDashboard(JSON.parse(data.layout_json));
        }
      } catch (err) {
        console.error("Could not load the dashboard:", err);
      }
    }

    fetchLayout();
  }, [accessToken]);

  // Superadmin görünümü
  if (isSuperAdmin) {
    const statCards = [
      { label: t("home.overview.edges"),    value: adminStats.edgeCount,    icon: "🖥️",  color: "var(--accent-navy-light)" },
      { label: t("home.overview.tenants"),  value: adminStats.tenantCount,  icon: "🏢",  color: "var(--success)" },
      { label: t("home.overview.users"),    value: adminStats.userCount,    icon: "👥",  color: "#d2a8ff" },
      { label: t("home.overview.vpnUsers"), value: adminStats.vpnUserCount, icon: "🔐",  color: "#ffa657" },
    ];

    return (
      <div style={styles.container}>
        <div style={{ marginBottom: "24px" }}>
          <h2 style={{ color: "var(--text-primary)", margin: 0, fontSize: "20px", fontWeight: 600 }}>
            {t("home.overview.title")}
          </h2>
          <p style={{ color: "var(--text-muted)", margin: "4px 0 0", fontSize: "13px" }}>
            {t("home.overview.subtitle")}
          </p>
        </div>

        <div style={styles.statsGrid}>
          {statCards.map((card) => (
            <div key={card.label} style={styles.statCard}>
              <div style={{ fontSize: "32px", marginBottom: "12px" }}>{card.icon}</div>
              <div style={{ fontSize: "36px", fontWeight: 700, color: card.color, lineHeight: 1 }}>
                {card.value === null ? "..." : card.value}
              </div>
              <div style={{ fontSize: "13px", color: "var(--text-muted)", marginTop: "8px" }}>
                {card.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container} ref={containerRef}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.breadcrumb}>
          <span>{t("home.header.home")}</span>
          <span style={styles.separator}>›</span>
          <span>{t("home.header.dashboards")}</span>
          <span style={styles.separator}>›</span>
          <span style={styles.active}>{t("home.header.current")}</span>
        </div>
        <div style={styles.controls}>
          {/* Not: her widget kendi edge'ine sabit (eklendiği anda seçilen edge/cihaz/tag).
              Burada tek bir "aktif edge" dropdown'u YOK -- widget'lar farklı edge'lerden
              olabilir, hepsi aynı dashboard'da yan yana durur. Edge seçimi sadece
              düzenleme modunda yeni widget eklerken yapılır (aşağıdaki panel). */}
          <select
            style={styles.select}
            aria-label={t("home.header.timeRangeAria")}
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
          >
            {["5m", "15m", "30m", "1h", "6h", "24h", "7d"].map((r) => (
              <option key={r} value={r}>{t(`home.header.range.${r}`)}</option>
            ))}
          </select>
          <span title={t("home.header.tzTitle")} style={{ fontSize: 11, color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
            {getViewerTimeZone()}
          </span>

          <button
            style={styles.refreshBtn}
            onClick={() => {
              fetchSensorData();
              fetchWidgetData();
            }}
          >
            {t("home.header.refresh")}
          </button>

          <select
            style={styles.select}
            aria-label={t("home.header.intervalAria")}
            value={refreshInterval}
            onChange={(e) => setRefreshInterval(e.target.value)}
          >
            {["off", "5s", "10s", "30s", "1m"].map((r) => (
              <option key={r} value={r}>{t(`home.header.interval.${r}`)}</option>
            ))}
          </select>

          <div style={styles.divider} />

          {canEditDashboard && (
            editMode ? (
              <>
                <button style={styles.saveBtn} onClick={saveDashboard}>
                  {t("home.actions.save")}
                </button>
                <button
                  style={styles.resetBtn}
                  onClick={() => setShowResetModal(true)}
                >
                  {t("home.actions.reset")}
                </button>
                <button
                  style={styles.cancelBtn}
                  onClick={() => setEditMode(false)}
                >
                  {t("home.actions.cancel")}
                </button>
              </>
            ) : (
              <button
                style={styles.editBtn}
                onClick={() => setEditMode(true)}
              >
                {t("home.actions.edit")}
              </button>
            )
          )}
        </div>
      </div>

      {/* Widget ekle: sağdan açılan 4 adımlı drawer (yalnızca tenant admin düzenleme modunda) */}
      {canEditDashboard && editMode && (
        <div style={styles.addPanel}>
          <button style={styles.saveBtn} disabled={edges.length === 0} onClick={() => setAddDrawerOpen(true)}>
            {t("home.actions.addWidget")}
          </button>
          {edges.length === 0 && (
            <span style={{ color: "var(--text-muted)", fontSize: "14px" }}>
              {t("home.info.noEdgesAdd")}
            </span>
          )}
        </div>
      )}
      <AddWidgetDrawer
        open={addDrawerOpen}
        onClose={() => setAddDrawerOpen(false)}
        accessToken={accessToken}
        edges={edges}
        selectedEdge={selectedEdge}
        onSelectEdge={(edge) => {
          setSelectedEdge(edge);
          setAddWidgetDeviceIds([]);
          setAddWidgetTagNames([]);
          if (edge) setSensorData((prev) => ({ ...prev, edgeId: edge.edgeId }));
        }}
        devices={dashboardDevices}
        devicesError={devicesError}
        deviceIds={addWidgetDeviceIds}
        setDeviceIds={setAddWidgetDeviceIds}
        tags={deviceTags}
        tagNames={addWidgetTagNames}
        setTagNames={setAddWidgetTagNames}
        widgetCount={(dashboard?.widgets ?? []).length}
        onAdd={(widget) => {
          setDashboard((prev) => ({ ...prev, widgets: [...(prev?.widgets ?? []), widget] }));
          setAddWidgetDeviceIds([]);
          setAddWidgetTagNames([]);
          toast.success(t("home.toast.widgetAdded"));
        }}
      />

      {canEditDashboard && editMode && (
        <div style={styles.editInfo}>
          <span>
            {t("home.info.editMode")}
          </span>
        </div>
      )}

      {/* Edge yoksa bilgi */}
      {!isSuperAdmin && edges.length === 0 && accessToken && (
        <div style={styles.editInfo}>
          <span style={{ color: "var(--text-muted)" }}>{t("home.info.noEdges")}</span>
        </div>
      )}

      {showResetModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>{t("home.reset.title")}</h3>
            <p style={styles.modalText}>{t("home.reset.text")}</p>
            <div style={styles.modalButtons}>
              <button
                style={styles.modalBtnYes}
                onClick={() => {
                  setDashboard(defaultDashboard);
                  setShowResetModal(false);
                }}
              >
                {t("home.actions.yes")}
              </button>
              <button style={styles.modalBtnNo} onClick={() => setShowResetModal(false)}>
                {t("home.actions.no")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Boş dashboard mesajı (edge ile aynı) veya grid */}
      {!dashboard?.widgets?.length ? (
        <div style={styles.emptyState}>
          <p style={styles.emptyText}>{t("home.empty.title")}</p>
          <p style={styles.emptyHint}>
            {isOperator ? t("home.empty.operator") : t("home.empty.admin")}
          </p>
        </div>
      ) : (
        <DashboardGrid
          widgets={dashboard.widgets}
          onLayoutChange={handleLayoutChange}
          onRemoveWidget={(id) =>
            setDashboard((prev) => ({
              ...prev,
              widgets: (prev?.widgets ?? []).filter((w) => w.id !== id),
            }))
          }
          sensorData={sensorData}
          widgetData={widgetData}
          onEventRangeChange={changeEventRange}
          editable={canEditDashboard && editMode}
          width={containerWidth - 48}
          cols={12}
          rowHeight={80}
        />
      )}
    </div>
  );
}

const styles = {
  container: {
    color: "var(--text-primary)",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    marginTop: "8px",
  },
  statCard: {
    backgroundColor: "var(--bg-surface)",
    border: "1px solid var(--border)",
    borderRadius: "12px",
    padding: "28px 24px",
    textAlign: "center",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
    flexWrap: "wrap",
    gap: "8px",
  },
  breadcrumb: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "var(--text-muted)",
    fontSize: "14px",
  },
  separator: {
    color: "var(--border)",
  },
  active: {
    color: "var(--text-primary)",
  },
  controls: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },
  select: {
    backgroundColor: "var(--bg-hover)",
    color: "var(--text-primary)",
    border: "1px solid var(--border)",
    borderRadius: "6px",
    padding: "5px 10px",
    fontSize: "12px",
    cursor: "pointer",
  },
  refreshBtn: {
    backgroundColor: "var(--bg-hover)",
    color: "var(--text-primary)",
    border: "1px solid var(--border)",
    borderRadius: "6px",
    padding: "5px 12px",
    fontSize: "12px",
    cursor: "pointer",
  },
  divider: {
    width: "1px",
    height: "24px",
    backgroundColor: "var(--border)",
    margin: "0 8px",
  },
  editBtn: {
    backgroundColor: "var(--btn-primary)",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    padding: "5px 12px",
    fontSize: "12px",
    cursor: "pointer",
  },
  saveBtn: {
    backgroundColor: "var(--btn-primary)",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    padding: "5px 12px",
    fontSize: "12px",
    cursor: "pointer",
  },
  resetBtn: {
    backgroundColor: "var(--danger)",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    padding: "5px 12px",
    fontSize: "12px",
    cursor: "pointer",
  },
  cancelBtn: {
    backgroundColor: "var(--bg-hover)",
    color: "var(--text-primary)",
    border: "1px solid var(--border)",
    borderRadius: "6px",
    padding: "5px 12px",
    fontSize: "12px",
    cursor: "pointer",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
  },
  modal: {
    backgroundColor: "var(--bg-popup)",
    borderRadius: "12px",
    padding: "32px",
    minWidth: "340px",
    boxShadow: "var(--shadow-md)",
  },
  modalTitle: {
    margin: "0 0 12px 0",
    fontSize: "20px",
    fontWeight: "600",
    color: "var(--text-primary)",
  },
  modalText: {
    margin: "0 0 24px 0",
    fontSize: "14px",
    color: "var(--text-secondary)",
  },
  modalButtons: {
    display: "flex",
    gap: "12px",
  },
  modalBtnYes: {
    padding: "8px 24px",
    backgroundColor: "var(--danger)",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
  },
  modalBtnNo: {
    padding: "8px 24px",
    backgroundColor: "var(--bg-hover)",
    color: "var(--text-primary)",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
  },
  editInfo: {
    backgroundColor: "rgba(15, 39, 68, 0.15)",
    border: "1px solid var(--accent-navy-light)",
    borderRadius: "6px",
    padding: "12px 16px",
    marginBottom: "16px",
    color: "var(--accent-navy-light)",
    fontSize: "13px",
  },
  addPanel: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
    marginBottom: "12px",
    padding: "12px",
    backgroundColor: "var(--bg-hover)",
    borderRadius: "8px",
    border: "1px solid var(--border)",
  },
  addPanelTitle: {
    color: "var(--text-muted)",
    fontSize: "13px",
  },
  addPanelLabel: {
    color: "var(--text-muted)",
    fontSize: "12px",
    marginLeft: "8px",
  },
  emptyState: {
    textAlign: "center",
    padding: "48px 24px",
    backgroundColor: "var(--bg-surface)",
    borderRadius: "8px",
    border: "1px dashed var(--border)",
  },
  emptyText: {
    color: "var(--text-primary)",
    fontSize: "16px",
    margin: "0 0 8px 0",
  },
  emptyHint: {
    color: "var(--text-muted)",
    fontSize: "14px",
    margin: 0,
  },
};
